<template>
  <Form class="card p-2 sm:p-4">
    <div v-if="step === 1" class="flex flex-col md:flex-row">
      <div class="flex flex-col justify-evenly w-full md:w-1/2 md:mr-8 mb-6 sm:mb-8">
        <div class="w-full flex flex-col justify-between">
          <label class="flex flex-start text-base sm:text-xl items-center">
            <font-awesome-icon icon="fa-pen" class="text-base sm:text-lg mr-2 sm:mr-3 shrink-0"/>
            {{ $t('PersonalGoals.qGoalName') }}
          </label>
          <InputText type="text" size="large" maxlength="1000" ref="goalName" v-model="goalName"
            :placeholder="$t('PersonalGoals.placeholder')" class="mt-1 text-sm sm:text-base" id="txt_goalName"/>
        </div>
        <div class="w-full flex flex-col justify-between mt-4">
          <label class="flex flex-start text-base sm:text-xl items-center">
            <font-awesome-icon icon="fa-flag-checkered" class="text-base sm:text-lg mr-2 sm:mr-3 shrink-0"/>
            {{ $t('PersonalGoals.qGoalType') }}
          </label>
          <Select v-model="goalType" :options="goalOptions" optionLabel="label" option-value="value"
            class="w-full mt-1 text-sm sm:text-lg" />
        </div>
          </div>
      <div class="flex flex-col justify-evenly w-full md:w-1/2 md:ml-8 text-sm sm:text-base">
          <p><b>{{ this.$t('PersonalGoals.goalType.habit') }}:</b>
          {{ this.$t('PersonalGoals.desc.habit') }}</p>
        <p><b>{{ this.$t('PersonalGoals.goalType.mindset') }}:</b>
          {{ this.$t('PersonalGoals.desc.mindset') }}</p>
        <p><b>{{ this.$t('PersonalGoals.goalType.reflection') }}:</b>
          {{ this.$t('PersonalGoals.desc.reflection') }}</p>
      </div>
    </div>
    <div v-if="step === 2">
      <div class="w-full flex flex-col justify-between mb-4 sm:mb-5">
        <label class="flex flex-start text-base sm:text-xl items-center">
          <font-awesome-icon icon="fa-bullseye" class="text-base sm:text-lg mr-2 sm:mr-3 shrink-0"/>
          {{ $t('PersonalGoals.qSpecific') }}
        </label>
        <InputText type="text" size="large" maxlength="500" ref="specific" v-model="specific"
          :placeholder="$t('PersonalGoals.placeholder')" class="mt-1 text-sm sm:text-base" id="txt_specific"/>
      </div>
      <div class="w-full flex flex-col justify-between mb-4 sm:mb-5">
        <label class="flex flex-start text-base sm:text-xl items-center">
          <font-awesome-icon icon="fa-clock" class="text-base sm:text-lg mr-2 sm:mr-3 shrink-0"/>
          {{ $t('PersonalGoals.qMeasurable') }}
        </label>
        <InputText type="text" size="large" maxlength="500" ref="measurable" v-model="measurable"
          :placeholder="$t('PersonalGoals.placeholder')" class="mt-1 text-sm sm:text-base" id="txt_measurable"/>
      </div>
      <div class="w-full flex flex-col justify-between mb-4 sm:mb-5">
        <label class="flex flex-start text-base sm:text-xl items-center">
          <font-awesome-icon icon="fa-certificate" class="text-base sm:text-lg mr-2 sm:mr-3 shrink-0"/>
          {{ $t('PersonalGoals.qAchievable') }}
        </label>
        <InputText type="text" size="large" maxlength="500" ref="achievable" v-model="achievable"
          :placeholder="$t('PersonalGoals.placeholder')" class="mt-1 text-sm sm:text-base" id="txt_achievable"/>
      </div>
      <div class="w-full flex flex-col justify-between mb-4 sm:mb-5">
        <label class="flex flex-start text-base sm:text-xl items-center">
          <font-awesome-icon icon="fa-magnifying-glass" class="text-base sm:text-lg mr-2 sm:mr-3 shrink-0"/>
          {{ $t('PersonalGoals.qRelevant') }}
        </label>
        <InputText type="text" size="large" maxlength="500" ref="relevant" v-model="relevant"
          :placeholder="$t('PersonalGoals.placeholder')" class="mt-1 text-sm sm:text-base" id="txt_relevant"/>
      </div>
      <div class="w-full flex flex-col justify-between mb-4 sm:mb-5">
        <label class="flex flex-start text-base sm:text-xl items-center">
          <font-awesome-icon icon="fa-calendar" class="text-base sm:text-lg mr-2 sm:mr-3 shrink-0"/>
          {{ $t('PersonalGoals.qGoalDate') }}
        </label>
        <div class="box-border p-0 max-w-[960px] my-0 mx-auto w-full">
          <div class="flex flex-wrap flex-col md:flex-row">
            <label for="cbox1" class="text-center block w-full max-w-80 py-1 px-3 mx-auto text-sm sm:text-lg font-medium
              text-[var(--color-orange)] cursor-pointer"
              id="lbl_calDate" @click.prevent="toggleShowCalendar">
                <font-awesome-icon icon="fa-calendar" class="shrink-0"/>
                {{ formattedStartDate }}
            </label>
            <div class="order-12 flex-grow w-full h-full p-1"
              id="div_calendar-date" v-if="getShowCalendar">
              <edit-kss-calendar-component :editable="editable" :initialDate="selectedDate"
                @updateDate="updateSelectedDate"/>
            </div>
          </div>
        </div>
      </div>
      <div class="w-full flex flex-col justify-between mb-4 sm:mb-5" v-if="this.goalType === this.$t('PersonalGoals.goalType.habit')">
        <label class="flex flex-start text-base sm:text-xl items-center">
          <font-awesome-icon icon="fa-bell" class="text-base sm:text-lg mr-2 sm:mr-3 shrink-0"/>
          {{ $t('PersonalGoals.setReminder') }}
        </label>
        <div class="w-full" id="div_runtime">
          <div class="flex flex-row justify-center items-center mt-4 sm:mt-5">
            <vee-field v-model="time" type="time" class="text-2xl sm:text-3xl font-bold"
              name="start-time" id="input_cal-time"/>
          </div>
          <div class="flex flex-col mt-3 items-center justify-around mb-4" id="drop_event">
            <div class="flex items-center justify-center flex-wrap mt-4 sm:mt-6 gap-1" id="seven_days">
              <label id="day" v-for="(day, index) in recurringDays" :key="day.name"
                @click="selectDay(index)" class="flex relative text-xs sm:text-sm w-fit">
                <div class="flex p-2 sm:p-4 rounded-lg m-0 cursor-pointer border-[1px]
                  border-[var(--color-light-grey)]"
        :class="{'text-[var(--color-orange)] border-[var(--color-orange)] bg-[var(--color-bisque)]'
                  : day.isSelected}">
                    <span :class="{'font-semibold': day.isSelected}">
                      {{ $t('PersonalGoals.daysShort.' + day.rSchName) }}</span>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="flex sm:my-8 sm:w-fit sm:float-right flex-col sm:flex-row w-full pt-6 sm:pt-8 items-center
        gap-3">
      <Button v-if="step === 1" @click="checkForErrorsStepOne" :disabled="disableNextButton"
        class="btn-primary-accent w-full sm:w-60 min-h-10 py-2 px-4 text-base sm:text-xl" unstyled
            id="btn_next">
        {{ $t('PersonalGoals.nextButton') }}
      </Button>
      <Button v-if="step === 2" @click="step = 1"
        class="btn-secondary w-full sm:w-60 min-h-10 py-2 px-4 text-base sm:text-xl" unstyled
            id="btn_back">
        {{ $t('PersonalGoals.backButton') }}
      </Button>
      <Button v-if="step === 2" @click.prevent="createPersonalGoal"
        class="btn-primary-accent w-full sm:w-60 min-h-10 py-2 px-4 text-base sm:text-xl" unstyled
            id="btn_createGoal">
        {{ $t('PersonalGoals.createGoal') }}
      </Button>
    </div>
  </Form>
</template>
<script>
import EditKssCalendarComponent
  from '@/components/Home/KssCreator/KssEditComponents/EditKssCalendarComponent.vue';
import { Form } from '@primevue/forms';
import { Button, InputText, Select } from 'primevue';

export default {
  name: 'PersonalGoalsFormComponent',
  props: {
    data: Object,
  },
  components: {
    EditKssCalendarComponent,
    Form,
    InputText,
    Select,
    Button,
  },
  data() {
    return {
      step: 1,
      showCalendar: false,
      selectedDate: new Date(),
      reconfirmation: false,
      showPopup: false,
      popupMessage: this.$t('PersonalGoals.popupMessage'),
      continueButtonText: this.$t('PersonalGoals.continue'),
      cancelButtonText: this.$t('PersonalGoals.cancel'),
      goalName: '',
      goalLength: '',
      specific: '',
      measurable: '',
      achievable: '',
      relevant: '',
      goalDate: '',
      goalType: this.$t('PersonalGoals.goalType.habit'),
      disableNextButton: (this.$store.getters.getActiveGoalsCount
        >= this.$store.getters.getPersonalGoalsMaxLimit),
      time: '12:00',
      // hour: '12',
      recurringDays: this.initRecurringDays([]),
      goalOptions: [
        { label: this.$t('PersonalGoals.goalType.habit'), value: this.$t('PersonalGoals.goalType.habit') },
        { label: this.$t('PersonalGoals.goalType.mindset'), value: this.$t('PersonalGoals.goalType.mindset') },
        { label: this.$t('PersonalGoals.goalType.reflection'), value: this.$t('PersonalGoals.goalType.reflection') },
        { label: this.$t('PersonalGoals.goalType.other'), value: this.$t('PersonalGoals.goalType.other') },
      ],
    };
  },
  beforeMount() {
    this.getPersonalGoalsLength();
  },
  computed: {
    formattedStartDate() {
      if (this.goalDate === '') {
        return `${(`0${this.selectedDate.getDate()}`).slice(-2)}.${(`0${this.selectedDate.getMonth() + 1}`).slice(-2)}.${this.selectedDate.getFullYear()}`;
      }
      return `${(`0${this.goalDate.getDate()}`).slice(-2)}.${(`0${this.goalDate.getMonth() + 1}`).slice(-2)}.${this.goalDate.getFullYear()}`;
    },
    editable() {
      return true;
    },
    getShowCalendar() {
      return this.showCalendar;
    },
    getDays() {
      return this.$store.getters.getRecurringDays;
    },
  },
  methods: {
    getPersonalGoalsLength() {
      const personalGoalsArray = this.$store.getters.getPersonalGoals;
      if (!personalGoalsArray) {
        this.goalLength = '0';
        return 0;
      }
      return personalGoalsArray.length;
    },
    closePopup() {
      this.$store.commit('removeFrontendOverlay', this.data.id);
      this.$emit('close-overlay');
    },
    onPopupCancel() {
      this.showPopup = false;
      this.reconfirmation = false;
    },
    onPopupConfirm() {
      this.showPopup = false;
      this.createPersonalGoal();
    },
    async createPersonalGoal() {
      try {
        if (this.checkForErrorsStepTwo()) {
          return;
        }
        if (!this.reconfirmation) {
          if (this.allFilled()) {
            // show modal to ask for reconfirmation
            this.showPopup = true;
            this.$confirm.require({
              message: this.popupMessage,
              rejectProps: {
                label: this.cancelButtonText,
                severity: 'secondary',
                outlined: true,
              },
              acceptProps: {
                label: this.continueButtonText,
              },
              accept: this.onPopupConfirm,
              reject: this.onPopupCancel,
            });
            this.reconfirmation = true;
            return;
          }
        }
        const data = {
          goalName: this.goalName,
          goalType: this.goalType,
          goalDate: this.goalDate,
          questionAndAnswer: [
            {
              question: this.$t('PersonalGoals.qSpecific'),
              answer: this.specific,
              answerRequired: false,
              linkToIcon: '',
              questionKeyword: this.$t('PersonalGoals.specific'),
            },
            {
              question: this.$t('PersonalGoals.qMeasurable'),
              answer: this.measurable,
              answerRequired: false,
              linkToIcon: '',
              questionKeyword: this.$t('PersonalGoals.measurable'),
            },
            {
              question: this.$t('PersonalGoals.qAchievable'),
              answer: this.achievable,
              answerRequired: false,
              linkToIcon: '',
              questionKeyword: this.$t('PersonalGoals.achievable'),
            },
            {
              question: this.$t('PersonalGoals.qRelevant'),
              answer: this.relevant,
              answerRequired: false,
              linkToIcon: '',
              questionKeyword: this.$t('PersonalGoals.relevant'),
            },
          ],
          reminderTime: {
            time: this.time,
            days: this.recurringDays,
          },
        };
        await this.$store.dispatch('createPersonalGoal', data);
        this.$toast.add({
          severity: 'success',
          summary: this.$t('PersonalGoals.successMsg'),
          detail: '',
          life: 3000,
        });
        this.$store.commit('setActiveGoalsCount', (this.$store.getters.getActiveGoalsCount + 1));
        await this.$store.dispatch('getPersonalGoals');
        this.closePopup();
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: error.response.data.message,
          detail: '',
          life: 3000,
        });
      }
    },
    allFilled() {
      return (this.specific.trim() === '' || this.measurable.trim() === '' || this.achievable.trim() === '' || this.relevant.trim() === '');
    },
    checkForErrorsStepOne() {
      let hasError = false;
      if (this.goalName.trim() === '') {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('PersonalGoals.enterName'),
          detail: '',
          life: 3000,
        });
        this.goalName = '';
        hasError = true;
      }
      if (this.goalType.trim() === '') {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('PersonalGoals.selectGoal'),
          detail: '',
          life: 3000,
        });
        hasError = true;
      }
      if (hasError) {
        return hasError;
      }
      this.step = 2;
      return hasError;
    },
    checkForErrorsStepTwo() {
      let hasError = false;
      const currentDate = new Date();
      if (this.goalDate - currentDate < 0) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('PersonalGoals.incorrectDate'),
          detail: '',
          life: 3000,
        });
        hasError = true;
      }
      if (this.goalType === 'Habit') {
        const len = this.recurringDays.filter((e) => e.isSelected).length;
        if (len === 0) {
          this.$toast.add({
            severity: 'error',
            summary: this.$t('PersonalGoals.selectRecurringDays'),
            detail: '',
            life: 3000,
          });
          hasError = true;
        }
      }

      return hasError;
    },
    toggleShowCalendar() {
      this.showCalendar = !this.showCalendar;
    },
    updateSelectedDate(date) {
      this.goalDate = date;
      this.toggleShowCalendar();
    },
    selectDay(index) {
      this.recurringDays[index].isSelected = !this.recurringDays[index].isSelected;
    },
    initRecurringDays(byDays) {
      return [
        {
          rSchName: 'MO',
          isSelected: byDays.includes('MO'),
        },
        {
          rSchName: 'TU',
          isSelected: byDays.includes('TU'),
        },
        {
          rSchName: 'WE',
          isSelected: byDays.includes('WE'),
        },
        {
          rSchName: 'TH',
          isSelected: byDays.includes('TH'),
        },
        {
          rSchName: 'FR',
          isSelected: byDays.includes('FR'),
        },
        {
          rSchName: 'SA',
          isSelected: byDays.includes('SA'),
        },
        {
          rSchName: 'SU',
          isSelected: byDays.includes('SU'),
        },
      ];
    },
  },
};
</script>

<style scoped>
.card {
    border: none !important;
    background-color: transparent;
}
</style>
